/**
 * Newsletter Modal - Showtime V2
 *
 * Shows a newsletter signup popup after a configurable delay.
 * Respects a localStorage-based cooldown so it doesn't reappear
 * within the configured number of days.
 *
 * Accessibility: focus trap, Esc to close, overlay click to close,
 * focus restoration, screen-reader announcements.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'showtime_newsletter_seen';
  var config = window.__NEWSLETTER_CONFIG__ || {};
  var delayMs = config.delay || 10000;
  var cooldownDays = config.cooldownDays || 10;

  var overlay = document.getElementById('newsletter-modal-overlay');
  var modal = document.getElementById('newsletter-modal');

  if (!overlay || !modal) return;

  var closeBtn = modal.querySelector('[data-newsletter-close]');
  var emailInput = modal.querySelector('input[type="email"]');
  var form = modal.querySelector('form');
  var previousFocus = null;
  var focusTrapCleanup = null;

  /**
   * Check if the popup should be shown based on localStorage cooldown.
   */
  function shouldShow() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return true;
      var ts = parseInt(stored, 10);
      if (isNaN(ts)) return true;
      var elapsed = Date.now() - ts;
      var cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;
      return elapsed > cooldownMs;
    } catch (e) {
      return true;
    }
  }

  /**
   * Mark the popup as seen (sets timestamp in localStorage).
   */
  function markSeen() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable
    }
  }

  /**
   * Open the modal.
   */
  function open() {
    previousFocus = document.activeElement;
    overlay.style.display = '';
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // ADA SW-6: Hide background content from assistive tech
    var mainContent = document.getElementById('main-content') || document.querySelector('main');
    var header = document.querySelector('header');
    var footer = document.querySelector('footer');
    if (mainContent) mainContent.inert = true;
    if (header) header.inert = true;
    if (footer) footer.inert = true;

    // Focus the email input after a frame so the browser can render
    requestAnimationFrame(function () {
      if (emailInput) emailInput.focus();
    });

    // Use theme's trapFocus if available, otherwise inline
    if (window.ShowtimeTheme && window.ShowtimeTheme.trapFocus) {
      focusTrapCleanup = window.ShowtimeTheme.trapFocus(modal);
    } else {
      focusTrapCleanup = inlineTrapFocus(modal);
    }

    // Announce to screen readers
    if (window.ShowtimeTheme && window.ShowtimeTheme.announce) {
      window.ShowtimeTheme.announce('Newsletter signup dialog opened');
    }
  }

  /**
   * Close the modal and mark as seen.
   */
  function close() {
    overlay.style.display = 'none';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // ADA SW-6: Restore background content to assistive tech
    var mainContent = document.getElementById('main-content') || document.querySelector('main');
    var header = document.querySelector('header');
    var footer = document.querySelector('footer');
    if (mainContent) mainContent.inert = false;
    if (header) header.inert = false;
    if (footer) footer.inert = false;

    markSeen();

    if (focusTrapCleanup) {
      focusTrapCleanup();
      focusTrapCleanup = null;
    }

    if (previousFocus && previousFocus.focus) {
      previousFocus.focus();
    }
  }

  /**
   * Minimal inline focus trap fallback.
   */
  function inlineTrapFocus(el) {
    var focusable = el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }

    el.addEventListener('keydown', handler);
    return function () {
      el.removeEventListener('keydown', handler);
    };
  }

  // --- Event listeners ---

  // Close button
  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  // Overlay click
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) close();
  });

  // Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') {
      close();
    }
  });

  // On successful form submit (Shopify returns the page with form.posted_successfully? true)
  if (config.formPostedSuccessfully) {
    markSeen();
    // Show the modal briefly to display the success message, then auto-close
    open();
    setTimeout(close, 3000);
    return; // Don't schedule the delayed open
  }

  // Mark seen on form submit (covers the page-reload gap)
  if (form) {
    form.addEventListener('submit', function () {
      markSeen();
    });
  }

  // --- Schedule popup ---
  if (shouldShow()) {
    setTimeout(open, delayMs);
  }
})();
